// Single source of truth for which navigation *chrome* is active: the
// horizontal top nav or the classic left sidebar.
//
// This used to be conflated with "is the modern page presentation on" (the old
// `useIsNewNav`). After the layout unification BOTH chromes render the same
// compact page bar and modern pages, so presentation is no longer a variable —
// only the chrome is. This module resolves the chrome once, synchronously.
//
// Order of precedence:
//   1. URL query `?newNavigation=true|false` (search OR hash query)
//   2. Demo mode forces the top nav (see isDemo)
//   3. localStorage key `dn_new_navigation` (explicit choice, or the fresh-browser
//      seed below which defaults a brand-new browser/login to the top nav)
//   4. Otherwise the classic sidebar
//
// The resolved value seeds `NavChromeProvider`, which owns the live state: a
// switch updates it in place, inside a View Transition, with no reload.

import { isDemo } from '@/config/apiUrls';

export type NavChrome = 'top' | 'sidebar';

export const NAV_CHROME_LS_KEY = 'dn_new_navigation';

const getNavParam = (): string | null => {
  if (typeof window === 'undefined') return null;
  const main = new URLSearchParams(window.location.search).get('newNavigation');
  if (main !== null) return main;
  const hashQuery = window.location.hash.includes('?')
    ? window.location.hash.slice(window.location.hash.indexOf('?') + 1)
    : '';
  return new URLSearchParams(hashQuery).get('newNavigation');
};

export const resolveNavChrome = (): NavChrome => {
  const param = getNavParam();
  if (param !== null) return param !== 'false' ? 'top' : 'sidebar';
  if (isDemo()) return 'top';
  if (typeof window !== 'undefined') {
    const stored = window.localStorage.getItem(NAV_CHROME_LS_KEY);
    if (stored !== null) return stored === 'true' ? 'top' : 'sidebar';
  }
  return 'sidebar';
};

/**
 * Default a FRESH browser/login to the top nav. This runs ONCE at module load — deliberately, not from the
 * login flow: `NavChromeProvider` seeds its state from `resolveNavChrome` at its first render, so a value
 * written during login lands too late for that session. Running here — before `Routes` ever calls `resolveNavChrome` — makes the default apply on this
 * same load, and persisting it makes it stick on later ones.
 *
 * "Fresh" = this browser has never logged into Datanomik. `useUser` persists the session under the `user`
 * key on every login and logout only rewrites it to an empty user (never removes it), so the key is present
 * on any returning browser (even logged out) and absent only on a genuinely fresh browser. An existing user
 * who never activated the new nav is thus recognised as returning and kept on the classic sidebar. Any
 * explicit stored choice (opt-in OR opt-out) wins and is left untouched, so reverts stick.
 */
const SESSION_STORAGE_KEY = 'user';
const seedFreshBrowserNavDefault = (): void => {
  if (typeof window === 'undefined') return;
  try {
    if (window.localStorage.getItem(NAV_CHROME_LS_KEY) !== null) return; // explicit choice wins
    if (window.localStorage.getItem(SESSION_STORAGE_KEY) !== null) return; // returning browser → sidebar
    window.localStorage.setItem(NAV_CHROME_LS_KEY, 'true'); // fresh browser/login → default to top nav
  } catch {
    // ignore storage failures — the browser simply stays on the default (sidebar) chrome
  }
};
seedFreshBrowserNavDefault();

/** Persist a chrome choice (used by the URL-param sticky behavior in routes). */
export const persistNavChromeFromParam = (): void => {
  if (typeof window === 'undefined') return;
  const param = getNavParam();
  if (param !== null) {
    window.localStorage.setItem(NAV_CHROME_LS_KEY, param !== 'false' ? 'true' : 'false');
  }
};
